import { createElement, forwardRef, type SVGProps, useEffect, useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { Counter } from '@factorialco/f0-react/dist/experimental'
import { EllipsisHorizontal, ShoppingCart } from '@factorialco/f0-react/icons/app'

import {
  useModuleHeaderActions,
  type ModuleHeaderDropdownItem,
} from 'modules/layout/components/ResponsivePage/hooks'

import i18n from 'modules/core/lib/i18n'

import { useDeviceCatalogCartContext } from 'modules/it_management/contexts/DeviceCatalogCartContext'

const SmallShoppingCart = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>(
  ({ style, ...props }, ref) =>
    createElement(ShoppingCart, { ...props, ref, style: { ...style, padding: '2px' } })
)

function buildCartIconWithCounter(counter: number) {
  const CartIconWithCounter = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>(
    ({ style, ...props }, ref) =>
      createElement(
        'span',
        { className: 'relative inline-flex items-center justify-center' },
        createElement(ShoppingCart, {
          ...props,
          ref,
          style: { ...style, padding: '2px' },
        }),
        createElement(
          'span',
          {
            className: 'pointer-events-none absolute -top-3 -right-3',
          },
          createElement(Counter, { value: counter, maxValue: 99, size: 'sm', type: 'bold' })
        )
      )
  )

  return CartIconWithCounter
}

const DEVICE_CATALOG_BASE = '/device-catalog'
const ORDERS_PATH = `${DEVICE_CATALOG_BASE}/orders`

/**
 * Registers the device catalog cart button and "..." menu in the PageHeader.
 * Call from DeviceCatalogRootPage so the actions appear in the header while in the device catalog.
 */
export function useDeviceCatalogHeaderActions(): void {
  const { setModuleHeaderActions } = useModuleHeaderActions()
  const { pathname } = useLocation()
  const { itemCount: cartItemCount } = useDeviceCatalogCartContext()

  // Set actions when pathname or count change. useLayoutEffect runs before paint to avoid blink.
  // No cleanup - clearing here caused a blink when navigating list → details.
  useLayoutEffect(() => {
    const cartLabel =
      cartItemCount > 0
        ? i18n.t('it_management.device_catalog.cart.header_button_with_count', {
            count: cartItemCount,
          })
        : i18n.t('it_management.device_catalog.cart.header_button')

    const moreMenuItems: ModuleHeaderDropdownItem[] = [
      { label: i18n.t('it_management.device_catalog.menu_orders'), href: ORDERS_PATH },
    ]

    setModuleHeaderActions([
      {
        label: cartLabel,
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        icon: (cartItemCount > 0
          ? buildCartIconWithCounter(cartItemCount)
          : SmallShoppingCart) as typeof ShoppingCart,
        href: `${pathname}#cart`,
      },
      {
        label: i18n.t('it_management.device_catalog.more_menu'),
        icon: EllipsisHorizontal,
        actions: moreMenuItems,
      },
    ])
  }, [pathname, cartItemCount, setModuleHeaderActions])

  // Clear only on unmount (when leaving device catalog)
  useEffect(() => {
    return () => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- temporary debug for cart blink investigation
        console.debug('[DeviceCatalogHeaderActions] unmount, clearing cart')
      }
      setModuleHeaderActions(null)
    }
  }, [setModuleHeaderActions])
}
